import React, { useEffect, useState } from 'react';
import { Users, Shield, FileText, Mail, Settings as SettingsIcon, Calendar as CalendarIcon } from 'lucide-react';
import axiosInstance from '../../../services/axiosInstance';
import apolloClient from '../../../services/apolloClient';
import { GET_DASHBOARD_STATS } from '../../../services/queries';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import type { AnomaliaTesseramento } from '../../../utils/tesseramentoAnomalie';

/* ============================================================
   Moduli dell'area amministrativa V2: un'unica definizione per
   Dashboard, sidebar e ricerca globale (parity con la dashboard
   classica: i 38 moduli della dashboard + le 5 pagine del menu classico).
   I dati (pendenze, contatori, ultimo backup) passano da una cache
   condivisa a livello di modulo: una lettura al minuto, riusata da
   tutte le pagine, così la navigazione non moltiplica le chiamate.
   ============================================================ */

export type AreaKey = 'assoc' | 'rc' | 'cont' | 'events' | 'com' | 'sys';

export const AREAS: Record<AreaKey, { label: string; badge: string; dotVar: string; bgVar: string; fgVar: string }> = {
  assoc: { label: 'Associati', badge: 'a2-badge--green', dotVar: 'var(--a2-green-500)', bgVar: 'var(--a2-hgrn)', fgVar: 'var(--a2-grn7)' },
  rc: { label: 'Rete Certificata', badge: 'a2-badge--cyan', dotVar: 'var(--a2-cyan-fg)', bgVar: 'var(--a2-cyan-bg)', fgVar: 'var(--a2-cyan-fg)' },
  cont: { label: 'Contenuti', badge: 'a2-badge--vio', dotVar: 'var(--a2-vio-fg)', bgVar: 'var(--a2-vio-bg)', fgVar: 'var(--a2-vio-fg)' },
  events: { label: 'Eventi', badge: 'a2-badge--warn', dotVar: 'var(--a2-warning)', bgVar: 'var(--a2-warn-bg)', fgVar: 'var(--a2-warn-fg)' },
  com: { label: 'Comunicazione', badge: 'a2-badge--info', dotVar: 'var(--a2-info-fg)', bgVar: 'var(--a2-info-bg)', fgVar: 'var(--a2-info-fg)' },
  sys: { label: 'Sistema', badge: 'a2-badge--chip', dotVar: 'var(--a2-fnt)', bgVar: 'var(--a2-chip)', fgVar: 'var(--a2-mut)' },
};

export const AREA_KEYS = Object.keys(AREAS) as AreaKey[];

export const AREA_ICONS: Record<AreaKey, React.ReactNode> = {
  assoc: <Users size={14} />,
  rc: <Shield size={14} />,
  cont: <FileText size={14} />,
  events: <CalendarIcon size={14} />,
  com: <Mail size={14} />,
  sys: <SettingsIcon size={14} />,
};

export interface ModuleDef {
  key: string;
  area: AreaKey;
  name: string;
  sub: string;
  val: string;
  pend: number;
  pendAmber?: boolean;
  href: string;
  visible: boolean;
}

/* Contatori della dashboard usati dai moduli (sottoinsieme di DashboardStats) */
export interface DashboardCounts {
  totalArticles?: number | null; publishedArticles?: number | null; totalUsers?: number | null; totalGalleries?: number | null;
  totalAssociates?: number | null; activeAssociates?: number | null; pendingMemberships?: number | null;
  totalCertifiedMembers?: number | null; activeCertifiedMembers?: number | null; pendingCertifiedApplications?: number | null;
}

export interface BackupInfo { name: string; size: number; createdAt: string }

/* —— Statistiche extra per moduli (stesse chiamate delle card classiche) —— */
export interface PaymentItem { _id: string; importoTotale?: number; associateId?: { _id?: string; codice?: string; nominativo?: string } }
export interface ExtraStats {
  memo?: { pending: number; overdue: number; unreadMessages: number };
  tickets?: { total: number; open: number };
  inbox?: { errors: number; enabled: boolean; dryRun: boolean };
  collab?: { total: number; new: number };
  reviews?: { total: number; completed: number };
  community?: { totalDiscussions: number; pendingReports: number };
  voucherPratiche?: { totale: number };
  atti?: { attive: number; problematiche: number };
  voucherRimborso?: { disponibili: number };
  tessPayments?: { pending: number };
  tessTodo?: {
    bonifici: number; richieste: number; duplicati: number; recenti: number; total: number;
    richiestaFirstAssocId?: string | null;
    richiesteItems?: Array<{ _id: string; settore?: string; associateId?: { _id: string; codice?: string; nominativo?: string } }>;
    bonificiItems?: PaymentItem[];
    recentiItems?: PaymentItem[];
    // Anomalie da verificare (assenti con backend precedente alla campagna rinnovi)
    anomalie?: number;
    anomalieItems?: AnomaliaTesseramento[];
  };
  representatives?: { count: number; needsReview: number };
  repRooms?: { count: number; active: number };
  assocPerms?: { total: number; assigned: number };
  events?: { total: number; upcoming: number };
  takata?: { aperte: number; esitoSbagliate: number };
  takataUso?: { totale: number; oggi: number };
}

export interface Perms {
  hasPermission: (p: string) => boolean;
  hasAnyPermission: (p: string[]) => boolean;
}

/** Una chiamata lenta non deve bloccare tutte le pendenze: oltre questo limite il pezzo resta vuoto. */
export const EXTRA_STATS_TIMEOUT_MS = 8000;

/**
 * Carica le pendenze dei moduli. `onPartial` riceve ogni pezzo appena arriva (lista progressiva);
 * la promise si risolve con l'insieme completo.
 */
export async function loadExtraStats({ hasPermission, hasAnyPermission }: Perms, onPartial?: (part: Partial<ExtraStats>) => void): Promise<ExtraStats> {
  const safe = async <T,>(fn: () => Promise<T | undefined>): Promise<T | undefined> => {
    try {
      return await Promise.race([fn(), new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), EXTRA_STATS_TIMEOUT_MS))]);
    } catch { return undefined; }
  };
  const tasks: Promise<Partial<ExtraStats>>[] = [];
  const push = (fn: () => Promise<Partial<ExtraStats> | undefined>) => tasks.push(safe(fn).then(v => { if (v && Object.keys(v).length && onPartial) onPartial(v); return v || {}; }));

  if (hasPermission('memos.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/memos/stats');
      return { memo: { pending: r.data.pending || 0, overdue: r.data.overdue || 0, unreadMessages: r.data.unreadMessages || 0 } };
    });
  }
  if (hasPermission('supporttickets.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/support-tickets/stats');
      return r.data.success ? { tickets: { total: r.data.data.total, open: r.data.data.open } } : {};
    });
    push(async () => {
      const r = await axiosInstance.get('/admin/support-inbox/status');
      const d = r.data?.data;
      return r.data.success ? { inbox: { errors: d?.counts?.error || 0, enabled: !!d?.enabled, dryRun: !!d?.dryRun } } : {};
    });
  }
  if (hasPermission('collaboration.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/collaborations/stats');
      return r.data.success ? { collab: { total: r.data.data.total, new: r.data.data.new } } : {};
    });
  }
  if (hasPermission('service-reviews.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/service-reviews/stats');
      return r.data.success ? { reviews: { total: r.data.stats.total, completed: r.data.stats.completed } } : {};
    });
  }
  if (hasAnyPermission(['community_v2.moderate', 'community_v2.manage'])) {
    push(async () => {
      const r = await axiosInstance.get('/admin/community/v2/stats');
      return r.data.success ? { community: { totalDiscussions: r.data.data.totalDiscussions, pendingReports: r.data.data.pendingReports } } : {};
    });
  }
  if (hasAnyPermission(['vouchers.read', 'vouchers.approve', 'vouchers.manage_all'])) {
    push(async () => {
      const r = await axiosInstance.get('/voucher-pratiche/stats');
      return r.data.success ? { voucherPratiche: { totale: r.data.stats.totale } } : {};
    });
  }
  if (hasPermission('atti.admin_view')) {
    push(async () => {
      const r = await axiosInstance.get('/atti-svincolo/admin/stats');
      if (!r.data.success) return {};
      const s = r.data.data;
      return { atti: { attive: s.generale.richiesteAttive, problematiche: s.problematiche.totale } };
    });
  }
  if (hasPermission('voucher.admin_view')) {
    push(async () => {
      const r = await axiosInstance.get('/voucher-rimborso/admin/stats');
      return r.data.success ? { voucherRimborso: { disponibili: r.data.data.generale.voucherDisponibili } } : {};
    });
  }
  if (hasPermission('associate-payments.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/associate-payments/stats');
      if (!r.data.success) return {};
      const d = r.data.data;
      return { tessPayments: { pending: (d?.bonificiInAttesa || 0) + (d?.rateInScadenza?.prossimi7giorni || 0) } };
    });
    push(async () => {
      const r = await axiosInstance.get('/admin/associate-payments/todo');
      if (!r.data.success) return {};
      const d = r.data.data;
      const bonifici = d?.bonificiDaConfermare?.count || 0;
      const richieste = d?.richiesteIntegrative?.count || 0;
      const duplicati = d?.possibiliDuplicati?.count || 0;
      const recenti = d?.pagamentiRecenti?.count || 0;
      // Prima richiesta integrativa: il modulo linka la scheda del SUO associato
      // (le richieste si gestiscono lì, non nella lista pagamenti).
      const richiestaFirstAssocId = d?.richiesteIntegrative?.items?.[0]?.associateId?._id || null;
      const richiesteItems = (d?.richiesteIntegrative?.items || []).slice(0, 3);
      const bonificiItems = (d?.bonificiDaConfermare?.items || []).slice(0, 3);
      const recentiItems = (d?.pagamentiRecenti?.items || []).slice(0, 3);
      const anomalieItems: AnomaliaTesseramento[] = Array.isArray(d?.anomalie?.items) ? d.anomalie.items : [];
      const anomalie = d?.anomalie?.count ?? anomalieItems.length;
      return { tessTodo: { bonifici, richieste, duplicati, recenti, total: bonifici + richieste + duplicati + anomalie, richiestaFirstAssocId, richiesteItems, bonificiItems, recentiItems, anomalie, anomalieItems } };
    });
  }
  if (hasPermission('representatives.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/representatives', { params: { limit: 1, status: 'attivo' } });
      return { representatives: { count: r.data.total || 0, needsReview: r.data.needsReviewCount || 0 } };
    });
  }
  if (hasPermission('representativecommunications.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/representative-communications/rooms');
      const rooms = r.data?.rooms || [];
      return { repRooms: { count: rooms.length, active: rooms.filter((x: any) => x.isActive).length } };
    });
  }
  if (hasPermission('associativeroles.update')) {
    push(async () => {
      const [permRes, rolesRes] = await Promise.all([
        axiosInstance.get('/admin/associative-roles/permissions/all'),
        axiosInstance.get('/admin/associative-roles'),
      ]);
      const permissions = permRes.data?.data || permRes.data || [];
      const roles = rolesRes.data?.data || rolesRes.data || [];
      const assigned = roles.reduce((acc: number, r: any) => acc + (r.permessi?.length || 0), 0);
      return { assocPerms: { total: Array.isArray(permissions) ? permissions.length : 0, assigned } };
    });
  }
  if (hasPermission('takata.segnalazioni_read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/takata-stato-portali/segnalazioni/stats');
      return r.data.success ? { takata: { aperte: r.data.aperte || 0, esitoSbagliate: r.data.esitoSbagliatoAperte || 0 } } : {};
    });
  }
  // Statistiche d'uso della verifica Takata (stessa chiamata della card classica)
  if (hasPermission('takata.stats_read')) {
    push(async () => {
      const { data } = await axiosInstance.get('/admin/takata-stats?giorni=7');
      if (!data.success) return {};
      const oggiIso = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
      const oggi = (data.perGiorno || []).find((g: { _id: string; n: number }) => g._id === oggiIso);
      return { takataUso: { totale: data.totale || 0, oggi: oggi ? oggi.n : 0 } };
    });
  }
  if (hasPermission('events.read')) {
    push(async () => {
      const r = await axiosInstance.get('/admin/events/dashboard-stats');
      return r.data.success ? { events: { total: r.data.stats.total || 0, upcoming: r.data.stats.upcoming || 0 } } : {};
    });
  }

  const results = await Promise.all(tasks);
  return Object.assign({}, ...results);
}

/* Contatori della dashboard per la sidebar/ricerca (stessa query GraphQL, senza attività) */
export async function loadDashboardCounts(): Promise<DashboardCounts | null> {
  try {
    const r = await apolloClient.query({ query: GET_DASHBOARD_STATS, variables: { activityLimit: 1 }, fetchPolicy: 'network-only', errorPolicy: 'all' });
    return r.data?.dashboardStats || null;
  } catch {
    return null;
  }
}

export async function loadLastBackup(): Promise<BackupInfo | null> {
  try {
    const r = await axiosInstance.get('/admin/backup/list', { headers: { 'x-backup-password': process.env.REACT_APP_BACKUP_PASSWORD || '' } });
    return r.data?.success && Array.isArray(r.data.data) && r.data.data.length > 0 ? r.data.data[0] : null;
  } catch {
    return null;
  }
}

/* —— Cache condivisa a livello di modulo: una lettura per chiave entro il TTL,
      richieste concorrenti deduplicate. —— */
interface CacheEntry<T> { value?: T; at: number; inflight?: Promise<T> }
const cache = new Map<string, CacheEntry<unknown>>();
export const SHARED_TTL = 60 * 1000;

export function getShared<T>(key: string, loader: () => Promise<T>, ttl = SHARED_TTL, now = Date.now()): Promise<T> {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (entry?.inflight) return entry.inflight;
  if (entry && entry.value !== undefined && now - entry.at < ttl) return Promise.resolve(entry.value);
  const inflight = loader().then(value => {
    cache.set(key, { value, at: Date.now() });
    return value;
  }, error => {
    cache.delete(key);
    throw error;
  });
  cache.set(key, { value: entry?.value, at: entry?.at ?? 0, inflight });
  return inflight;
}

export function peekShared<T>(key: string): T | undefined {
  return (cache.get(key) as CacheEntry<T> | undefined)?.value;
}

/** Solo per i test: svuota la cache condivisa. */
export function resetAdminSharedCache(): void {
  cache.clear();
}

export function useShared<T>(key: string, loader: () => Promise<T>, ttl = SHARED_TTL): T | undefined {
  const [value, setValue] = useState<T | undefined>(() => peekShared<T>(key));
  useEffect(() => {
    let alive = true;
    setValue(peekShared<T>(key));
    getShared(key, loader, ttl).then(v => { if (alive) setValue(v); }).catch(() => {});
    return () => { alive = false; };
    // Il loader è stabile per chiave: ricaricare solo al cambio di chiave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ttl]);
  return value;
}

const useUserKey = (): string => {
  const { user } = useAuth();
  return user?.username || 'anon';
};

export function useSharedExtraStats(): ExtraStats {
  return useSharedExtraStatsState().extra;
}

/** Oltre questo tempo lo scheletro lascia il posto a ciò che è già arrivato. */
export const SKELETON_MAX_MS = 2500;

/**
 * Come useSharedExtraStats, con caricamento progressivo (ogni pezzo compare appena arriva) e
 * `loaded` per gli scheletri: falso finché tutto è arrivato, ma al massimo per SKELETON_MAX_MS.
 */
export function useSharedExtraStatsState(): { extra: ExtraStats; loaded: boolean } {
  const perms = usePermissions();
  const userKey = useUserKey();
  const [partial, setPartial] = useState<Partial<ExtraStats>>({});
  const [timedOut, setTimedOut] = useState(false);
  const value = useShared<ExtraStats>(`extra:${userKey}`, () => loadExtraStats(perms, part => setPartial(prev => ({ ...prev, ...part }))));
  useEffect(() => {
    if (value !== undefined) return;
    const t = window.setTimeout(() => setTimedOut(true), SKELETON_MAX_MS);
    return () => window.clearTimeout(t);
  }, [value]);
  return { extra: value ?? partial, loaded: value !== undefined || timedOut };
}

export function useSharedDashboardCounts(): DashboardCounts | null {
  const userKey = useUserKey();
  return useShared<DashboardCounts | null>(`counts:${userKey}`, loadDashboardCounts) ?? null;
}

export function useSharedLastBackup(): BackupInfo | null {
  const userKey = useUserKey();
  return useShared<BackupInfo | null>(`backup:${userKey}`, loadLastBackup) ?? null;
}

/* —— I 44 moduli: i 39 della dashboard classica + Attività, Categorie, Ruoli, Impostazioni, Assistente (prima solo nel menu del sito) —— */
export interface BuildModulesInput extends Perms {
  stats: DashboardCounts | null;
  extra: ExtraStats;
  lastBackup: BackupInfo | null;
}

const fmt = (n: number | undefined | null) => (n === undefined || n === null ? '' : String(n));

export function buildModules({ stats, extra, lastBackup, hasPermission, hasAnyPermission }: BuildModulesInput): ModuleDef[] {
  const backupVal = lastBackup
    ? (() => { const d = new Date(lastBackup.createdAt); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`; })()
    : '—';
  const backupSub = lastBackup
    ? (() => { const d = new Date(lastBackup.createdAt); return `Ultimo: ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()
    : 'Nessun backup';

  return [
    // ── Associati ──
    { key: 'associates', area: 'assoc', name: 'Associati', sub: `${fmt(stats?.activeAssociates)} attivi`, val: fmt(stats?.totalAssociates), pend: 0, href: '/admin/associates', visible: hasPermission('associates.read') },
    { key: 'memberships', area: 'assoc', name: 'Domande Assoc.', sub: 'In sospeso', val: '', pend: stats?.pendingMemberships || 0, href: '/admin/memberships', visible: hasPermission('memberships.read') },
    { key: 'fees', area: 'assoc', name: 'Quote', sub: 'Tesseramento', val: '', pend: 0, href: '/admin/quote-associative', visible: hasPermission('associate-fees.read') },
    { key: 'tesspay', area: 'assoc', name: 'Pagamenti', sub: 'Tesseramento', val: '', pend: extra.tessPayments?.pending || 0, href: '/admin/pagamenti-tesseramento', visible: hasPermission('associate-payments.read') },
    { key: 'tesstodo', area: 'assoc', name: 'Tesseramento da gestire', sub: extra.tessTodo?.richieste ? `${extra.tessTodo.richieste} richieste integrative` : 'Bonifici, integrative, doppi', val: '', pend: extra.tessTodo?.total || 0, pendAmber: true, href: (extra.tessTodo?.richieste && extra.tessTodo?.richiestaFirstAssocId) ? `/admin/associates/${extra.tessTodo.richiestaFirstAssocId}` : '/admin/pagamenti-tesseramento', visible: hasPermission('associate-payments.read') },
    { key: 'representatives', area: 'assoc', name: 'Rappresentanti', sub: extra.representatives?.needsReview ? `${extra.representatives.needsReview} da rivedere` : 'Cariche associative', val: fmt(extra.representatives?.count), pend: extra.representatives?.needsReview || 0, pendAmber: true, href: '/admin/rappresentanti', visible: hasPermission('representatives.read') },
    { key: 'cariche', area: 'assoc', name: 'Cariche', sub: 'Ruoli associativi', val: '', pend: 0, href: '/admin/cariche-associative', visible: hasPermission('associativeroles.read') },
    { key: 'assocperms', area: 'assoc', name: 'Permessi Associativi', sub: `${fmt(extra.assocPerms?.assigned) || 0} assegnati`, val: fmt(extra.assocPerms?.total), pend: 0, href: '/admin/permessi-associativi', visible: hasPermission('associativeroles.update') },
    { key: 'memos', area: 'assoc', name: 'To Do', sub: extra.memo?.overdue ? `${extra.memo.overdue} scadut${extra.memo.overdue === 1 ? 'o' : 'i'}` : 'Memo admin', val: '', pend: extra.memo?.pending || 0, href: '/admin/memos', visible: hasPermission('memos.read') },
    // ── Rete Certificata ──
    { key: 'certified', area: 'rc', name: 'Certificati', sub: `${fmt(stats?.activeCertifiedMembers)} attivi`, val: fmt(stats?.totalCertifiedMembers), pend: 0, href: '/admin/certified-network/members', visible: hasPermission('certified.read') },
    { key: 'certapps', area: 'rc', name: 'Domande Cert.', sub: 'In sospeso', val: '', pend: stats?.pendingCertifiedApplications || 0, href: '/admin/certified-network', visible: hasPermission('certified.read') },
    { key: 'payrc', area: 'rc', name: 'Pagamenti RC', sub: 'Rete Certificata', val: '', pend: 0, href: '/admin/payments', visible: hasPermission('payments.read') },
    { key: 'tariffs', area: 'rc', name: 'Tariffari', sub: 'Pratiche', val: fmt(extra.voucherPratiche?.totale), pend: 0, href: '/admin/voucher/pratiche', visible: hasAnyPermission(['vouchers.read', 'vouchers.approve', 'vouchers.manage_all']) },
    { key: 'collab', area: 'rc', name: 'Collaborazioni', sub: 'Rete Certificata', val: fmt(extra.collab?.total), pend: extra.collab?.new || 0, href: '/admin/collaborazioni', visible: hasPermission('collaboration.read') },
    { key: 'atti', area: 'rc', name: 'Svincolo Atti', sub: 'In sospeso', val: fmt(extra.atti?.attive), pend: extra.atti?.problematiche || 0, href: '/admin/svincolo-atti', visible: hasPermission('atti.admin_view') },
    { key: 'voucherrimb', area: 'rc', name: 'Voucher Rimborso', sub: 'Disponibili', val: fmt(extra.voucherRimborso?.disponibili), pend: 0, href: '/admin/voucher-rimborso', visible: hasPermission('voucher.admin_view') },
    { key: 'reprooms', area: 'rc', name: 'Stanze Rappresentanti', sub: `${extra.repRooms?.active ?? 0} attive`, val: fmt(extra.repRooms?.count), pend: 0, href: '/admin/rappresentanti-stanze', visible: hasPermission('representativecommunications.read') },
    { key: 'activities', area: 'rc', name: 'Attività', sub: 'Formative e informative', val: '', pend: 0, href: '/admin/activities', visible: hasPermission('activities.read') },
    // ── Contenuti ed eventi ──
    { key: 'articles', area: 'cont', name: 'Articoli', sub: `${fmt(stats?.publishedArticles)} pubbl.`, val: fmt(stats?.totalArticles), pend: 0, href: '/admin/articles', visible: hasPermission('articles.read') },
    { key: 'galleries', area: 'cont', name: 'Gallerie', sub: 'Foto', val: fmt(stats?.totalGalleries), pend: 0, href: '/admin/galleries', visible: hasPermission('galleries.read') },
    { key: 'surveys', area: 'cont', name: 'Sondaggi', sub: 'Gestione', val: '', pend: 0, href: '/admin/surveys', visible: hasPermission('surveys.read') },
    { key: 'reviews', area: 'cont', name: 'Recensioni', sub: `Da moderare: ${extra.reviews?.completed ?? 0}`, val: fmt(extra.reviews?.total), pend: extra.reviews?.completed || 0, href: '/admin/service-reviews', visible: hasPermission('service-reviews.read') },
    { key: 'events', area: 'events', name: 'Eventi QR', sub: `${extra.events?.upcoming ?? 0} in arrivo`, val: fmt(extra.events?.total), pend: 0, href: '/admin/events', visible: hasPermission('events.read') },
    { key: 'eventtypes', area: 'events', name: 'Tipologie Evento', sub: 'Gestisci categorie', val: '', pend: 0, href: '/admin/event-types', visible: hasPermission('eventtypes.read') },
    { key: 'live', area: 'events', name: 'Dirette Live', sub: 'Gestione', val: '', pend: 0, href: '/admin/dirette', visible: hasPermission('live.read') },
    { key: 'verbali', area: 'events', name: 'Verbali', sub: 'Pubblicati, sola lettura', val: '', pend: 0, href: '/admin/verbali', visible: hasPermission('verbali.read') },
    { key: 'community', area: 'cont', name: 'Community', sub: 'Discussioni', val: fmt(extra.community?.totalDiscussions), pend: extra.community?.pendingReports || 0, href: '/admin/community-v2', visible: hasAnyPermission(['community_v2.moderate', 'community_v2.manage']) },
    { key: 'categories', area: 'cont', name: 'Categorie', sub: 'Articoli', val: '', pend: 0, href: '/admin/categories', visible: hasAnyPermission(['categories.update', 'categories.create', 'categories.delete']) },
    // ── Comunicazione ──
    { key: 'circolari', area: 'com', name: 'Circolari', sub: 'Archivio e invio', val: '', pend: 0, href: '/admin/circolari', visible: hasPermission('circolari.read') },
    { key: 'chat', area: 'com', name: 'Chat associati', sub: 'Canali e moderazione', val: '', pend: 0, href: '/admin/chat', visible: hasPermission('chat.read') },
    { key: 'mailings', area: 'com', name: 'Comunicazioni', sub: 'Email campagne', val: '', pend: 0, href: '/admin/communications', visible: hasPermission('mailings.read') },
    { key: 'recipients', area: 'com', name: 'Destinatari', sub: 'Gruppi', val: '', pend: 0, href: '/admin/recipient-groups', visible: hasPermission('recipient_groups.read') },
    { key: 'emailanalytics', area: 'com', name: 'Email Analytics', sub: 'Aperture e click', val: '', pend: 0, href: '/admin/email-analytics', visible: hasPermission('email.analytics') },
    { key: 'push', area: 'com', name: 'Push', sub: 'Notifiche', val: '', pend: 0, href: '/admin/push-center', visible: hasPermission('push.manage') },
    // Centro Ticket: unica card per tutte le richieste (ha assorbito il vecchio Feedback).
    { key: 'tickets', area: 'com', name: 'Centro Ticket', sub: 'Tutte le richieste', val: fmt(extra.tickets?.total), pend: extra.tickets?.open || 0, href: '/admin/centro-ticket', visible: hasPermission('supporttickets.read') },
    { key: 'posta', area: 'com', name: 'Posta supporto', sub: extra.inbox?.enabled === false ? 'Casella non configurata' : extra.inbox?.dryRun ? 'Modalità prova' : 'Email in ingresso', val: '', pend: extra.inbox?.errors || 0, href: '/admin/posta-supporto', visible: hasPermission('supporttickets.read') },
    // Le segnalazioni "esito sbagliato" valgono un possibile certificato emesso con esito
    // errato: quando ce ne sono il badge resta ROSSO (default) e il sottotitolo le nomina.
    { key: 'takataportali', area: 'com', name: 'Stato portali Takata', sub: extra.takata?.esitoSbagliate ? `${extra.takata.esitoSbagliate} con esito sbagliato` : 'Segnalazioni e controlli', val: '', pend: extra.takata?.aperte || 0, pendAmber: !extra.takata?.esitoSbagliate, href: '/admin/takata/stato-portali', visible: hasPermission('takata.segnalazioni_read') },
    { key: 'takatauso', area: 'com', name: 'Verifiche Takata', sub: extra.takataUso ? `${extra.takataUso.oggi.toLocaleString('it-IT')} oggi` : 'Statistiche d\'uso', val: extra.takataUso ? extra.takataUso.totale.toLocaleString('it-IT') : '', pend: 0, href: '/admin/takata/stats', visible: hasPermission('takata.stats_read') },
    { key: 'support', area: 'com', name: 'Supporto', sub: 'Canali e ticket', val: '', pend: 0, href: '/admin/canali-supporto', visible: hasPermission('supportgroups.manage') },
    // ── Sistema ──
    { key: 'backup', area: 'sys', name: 'Backup DB', sub: `${backupSub}`, val: backupVal !== '—' ? backupVal : '', pend: 0, href: '/admin/backup', visible: hasPermission('system.backup') },
    { key: 'sysanalysis', area: 'sys', name: 'Analisi Sistema', sub: 'Audit database', val: '', pend: 0, href: '/admin/system-analysis', visible: hasPermission('system.analysis') },
    { key: 'users', area: 'sys', name: 'Utenti', sub: 'Registrati', val: fmt(stats?.totalUsers), pend: 0, href: '/admin/users', visible: hasPermission('users.read') },
    { key: 'changelog', area: 'sys', name: 'Changelog', sub: 'Novità sistema', val: '', pend: 0, href: '/admin/changelog', visible: hasAnyPermission(['dashboard.view', 'dashboard.articles', 'dashboard.galleries', 'dashboard.users', 'dashboard.certified', 'dashboard.associates']) },
    { key: 'roles', area: 'sys', name: 'Ruoli e permessi', sub: 'Utenti amministrazione', val: '', pend: 0, href: '/admin/roles', visible: hasPermission('roles.read') },
    { key: 'settings', area: 'sys', name: 'Impostazioni', sub: 'Piattaforma', val: '', pend: 0, href: '/admin/settings', visible: hasPermission('settings.update') },
    { key: 'assistant', area: 'sys', name: 'Assistente AI', sub: 'Operazioni in linguaggio naturale', val: '', pend: 0, href: '/admin/assistente', visible: hasPermission('assistant.use') },
  ];
}

/** Moduli visibili all'utente con pendenze aggiornate: per sidebar e ricerca su ogni pagina V2. */
export function useAdminModules(): { modules: ModuleDef[]; extra: ExtraStats } {
  const perms = usePermissions();
  const extra = useSharedExtraStats();
  const stats = useSharedDashboardCounts();
  const lastBackup = useSharedLastBackup();
  const modules = buildModules({ stats, extra, lastBackup, hasPermission: perms.hasPermission, hasAnyPermission: perms.hasAnyPermission }).filter(m => m.visible);
  return { modules, extra };
}
